import {
  Component,
  Inject,
  Injector,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  Type,
  ViewChild,
} from '@angular/core';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { DataService } from 'src/app/services/data.service';
import { PlaybackController } from '../../contracts/playback-controller';
import { ToolbarDragService } from '../toolbar-drag.service';
import { ToolbarDrawerService } from '../toolbar-drawer.service';
import {
  SensorToolbarConfig,
  ToolbarControl,
  ToolbarControlContext,
  ToolbarControlDescriptor,
  ToolbarSlot,
  TOOLBAR_CONTROLS,
  TOOLBAR_CONTROL_CONTEXT,
} from '../../contracts/toolbar-control.tokens';

interface ResolvedControl {
  component: Type<ToolbarControl>;
  injector: Injector;
  order: number;
}

/**
 * Thin toolbar HOST. Renders an ordered, per-sensor-filtered list of control
 * plugins for a given slot. Deciding which controls appear is pure configuration
 * (`SensorToolbarConfig`); adding a control means registering a descriptor
 * (`TOOLBAR_CONTROLS`) — no edits here.
 *
 * Angular 14 note: `NgComponentOutlet` cannot bind inputs, so each control receives
 * its `ToolbarControlContext` through a dedicated child `Injector`.
 */
@Component({
  selector: 'app-toolbar-host',
  templateUrl: './toolbar-host.component.html',
  styleUrls: ['./toolbar-host.component.scss'],
})
export class ToolbarHostComponent implements OnChanges, OnDestroy {
  @Input() slot: ToolbarSlot = 'playback';
  @Input() controller!: PlaybackController;
  @Input() config!: SensorToolbarConfig | null;

  /**
   * CSS selector for the element the bar may be dragged within, resolved with
   * `closest()` from the bar itself.
   *
   * The Leaflet container rather than `#map`: this host is deliberately generic
   * (see `dark`, below) and a hard-coded id would tie it to the one page it
   * happens to be mounted on today. An unmatched selector simply leaves the drag
   * unconstrained, so a bar rendered outside a map still drags.
   */
  @Input() dragBoundary = '.leaflet-container';

  resolved: ResolvedControl[] = [];

  /** True between drag start and drag end — suppresses the grip's tooltip. */
  dragging = false;

  /** The bar's own drag directive — the only thing that can put it back. */
  @ViewChild(CdkDrag) private dragRef?: CdkDrag;

  /**
   * Dark ground, as a CLASS ON THE BAR rather than only as a `.dark-theme`
   * ancestor selector.
   *
   * Its sidebar siblings can rely on the body class alone because they live
   * inside the panel, which is painted by the same global sheet. This bar does
   * not: it renders inside the Leaflet map, whose subtree the theme sheet never
   * touches, and it is the piece most likely to be reused somewhere the body
   * class is absent (a preview, a report capture, an embed). Reading the same
   * `DataService` stream the map component itself subscribes to makes the bar's
   * ground depend on the app's theme state instead of on where it happens to be
   * mounted. The `.dark-theme` selector is kept alongside it, so whichever
   * signal is present wins and the two can never disagree into a light bar on a
   * dark page.
   */
  dark = false;

  private _themeSub?: Subscription;

  constructor(
    @Inject(TOOLBAR_CONTROLS) private registry: ToolbarControlDescriptor[],
    private parentInjector: Injector,
    private data: DataService,
    /** Public: the template binds `drag.position` straight to cdkDrag. */
    public drag: ToolbarDragService,
    private drawer: ToolbarDrawerService,
  ) {
    // BehaviorSubject-backed, so this emits the current theme synchronously —
    // the bar is never painted light for a frame before correcting itself.
    this._themeSub = this.data.darkTheme.subscribe((v) => (this.dark = !!v));
  }

  ngOnChanges(): void {
    this._resolve();
  }

  ngOnDestroy(): void {
    this._themeSub?.unsubscribe();
  }

  /**
   * A drawer is anchored to the button that opened it and does not follow a
   * moving bar, so it is dismissed rather than left floating over the map beside
   * the control it belongs to. The grip's tooltip is anchored the same way but
   * DOES follow the bar, so it is suppressed for the length of the gesture.
   */
  onDragStarted(): void {
    this.dragging = true;
    this.drawer.close();
  }

  /** Remember where the bar was left, so the next bar to mount starts there. */
  onDragEnded(event: CdkDragEnd): void {
    this.dragging = false;
    this.drag.set(event.source.getFreeDragPosition());
  }

  /**
   * Double-click the grip to send the bar home.
   *
   * The one way back from a position that has stopped making sense — a window
   * resized narrower after the bar was parked at the right edge leaves it
   * clamped somewhere unhelpful, and dragging it back is not always possible.
   *
   * Through the directive, not the stored offset: cdkDrag applies
   * `cdkDragFreeDragPosition` when that binding's REFERENCE changes, and the
   * store is mutated in place (see `ToolbarDragService`). `reset()` is what
   * actually clears the transform; the store is cleared alongside it so the next
   * bar to mount also starts at the bottom centre.
   */
  resetPosition(): void {
    this.dragRef?.reset();
    this.drag.reset();
  }

  private _resolve(): void {
    if (!this.controller || !this.config) {
      this.resolved = [];
      return;
    }
    // A lookup, not a ternary chain: the old chain sent any slot it did not
    // name to `playback`, so a newly added slot rendered the playback controls
    // without erroring — silently wrong. With the slot as the key, a new slot
    // is either configured or empty, never someone else's bar.
    const slotConfig = this.config[this.slot] ?? [];
    // The operator's declared abilities — the third gate beside descriptor
    // existence and slot whitelisting. Read off the CONFIG, not the controller:
    // the controller driving this bar varies by slot (the track bar runs on the
    // track controller), while the config is per-operator whichever bar asks.
    const caps = new Set(this.config.capabilities ?? []);
    const operator = this.config.operatorKey;

    this.resolved = slotConfig
      .filter((c) => c.enabled !== false)
      .map((c): ResolvedControl | null => {
        // OWNERSHIP is part of the match, not a check after it. An owned
        // descriptor answers only to its own operator; an unowned one answers
        // to anyone. Folded into `find` rather than applied to its result
        // because two descriptors can share an id — that is the whole point —
        // and a `find` that stopped at the first id match would resolve to the
        // wrong provider's control and then reject it, leaving the right one
        // unfound.
        const desc = this.registry.find(
          (d) =>
            d.id === c.id &&
            d.slots.includes(this.slot) &&
            (d.operatorKey === undefined || d.operatorKey === operator),
        );
        if (!desc) {
          this._warnUnresolved(c.id);
          return null;
        }
        const missing = (desc.requires ?? []).filter((cap) => !caps.has(cap));
        if (missing.length) {
          this._warnMissingCapability(c.id, missing);
          return null;
        }
        const context: ToolbarControlContext = {
          controller: this.controller,
          slot: this.slot,
          options: c.options ?? {},
        };
        const injector = Injector.create({
          providers: [{ provide: TOOLBAR_CONTROL_CONTEXT, useValue: context }],
          parent: this.parentInjector,
        });
        return {
          component: desc.component,
          injector,
          order: c.order ?? desc.defaultOrder,
        };
      })
      .filter((r): r is ResolvedControl => r !== null)
      .sort((a, b) => a.order - b.order);
  }

  /**
   * Already-reported `slot/id` pairs. Static rather than per-instance: four bars
   * mount for one sensor and each re-resolves on every `@Input` change, so
   * without this a single bad entry reports on every pass, from every slot.
   */
  private static readonly _reported = new Set<string>();

  /**
   * A config entry that matches no descriptor renders nothing, and did so in
   * SILENCE. Both ways of reaching that state have already shipped: the
   * port-call button sat on the playback bar for a release doing nothing, and
   * the popup's radar button was dead from the day it was written. Neither is
   * visible in a diff — the registration reads as complete and the control
   * simply never appears — and neither throws, so nothing surfaces it at all.
   *
   * The causes need different fixes, so they are reported apart: an id that is
   * not registered is a missing descriptor in `TOOLBAR_CONTROLS`; an id that is
   * registered but not for THIS slot is a `slots` whitelist that disagrees with
   * `SENSOR_TOOLBAR_CONFIGS`; and an id registered only under ANOTHER
   * operator's key is a control this operator has not written yet. The last two
   * both read as correct in each file while being wrong across them, and the
   * ownership one is the easiest to misread as the missing-descriptor case —
   * the id IS in the registry, just not for you.
   *
   * Dev mode only, and a warning rather than a throw: a control that fails to
   * resolve costs a button, which is not a reason to take the map down in front
   * of a user.
   */
  private _warnUnresolved(id: string): void {
    if (!isDevMode()) return;
    const key = `${this.config?.operatorKey ?? '?'}/${this.slot}/${id}`;
    if (ToolbarHostComponent._reported.has(key)) return;
    ToolbarHostComponent._reported.add(key);

    const operator = this.config?.operatorKey;
    const where = `${operator ?? 'unknown operator'} · '${this.slot}' slot`;
    const byId = this.registry.filter((d) => d.id === id);
    // Owned by someone else is asked FIRST: a control that is both wrong-slot
    // and wrong-operator is not this operator's control at all, so telling them
    // to widen its `slots` would send them to edit another provider's file.
    const mine = byId.filter(
      (d) => d.operatorKey === undefined || d.operatorKey === operator,
    );
    const owners = byId
      .map((d) => d.operatorKey)
      .filter((k): k is string => !!k);

    console.warn(
      !byId.length
        ? `[toolbar] '${id}' is configured for ${where} but no descriptor is registered ` +
            `under that id. Add one to TOOLBAR_CONTROLS (and declare the component in the ` +
            `module), or drop the entry from SENSOR_TOOLBAR_CONFIGS.`
        : !mine.length
          ? `[toolbar] '${id}' is configured for ${where} but every descriptor under that ` +
              `id belongs to [${owners.join(', ')}]. A control with an \`operatorKey\` is ` +
              `that provider's own — ${operator ?? 'this operator'} needs its own descriptor ` +
              `(same id, its own component, its own \`operatorKey\`), or the config entry ` +
              `should be dropped.`
          : `[toolbar] '${id}' is configured for ${where} but its descriptor whitelists ` +
              `only [${mine[0].slots.join(', ')}]. Add '${this.slot}' to its \`slots\` in ` +
              `TOOLBAR_CONTROLS, or drop the entry from SENSOR_TOOLBAR_CONFIGS. ` +
              `The control renders nothing until the two agree.`,
    );
  }

  /**
   * The THIRD way a configured control can silently not render: the descriptor
   * exists, the slot is whitelisted, but the operator has not declared a
   * capability the control needs. Reported apart from the two above because the
   * fix is different again — either the operator's `capabilities` array is
   * missing a capability it genuinely has, or the config entry asks for a
   * control the operator cannot honour and should be dropped.
   */
  private _warnMissingCapability(id: string, missing: string[]): void {
    if (!isDevMode()) return;
    const key = `${this.config?.operatorKey ?? '?'}/${this.slot}/${id}/caps`;
    if (ToolbarHostComponent._reported.has(key)) return;
    ToolbarHostComponent._reported.add(key);

    console.warn(
      `[toolbar] '${id}' is configured for ${this.config?.operatorKey ?? 'unknown operator'} · ` +
        `'${this.slot}' slot but requires [${missing.join(', ')}], which its ` +
        `SensorToolbarConfig.capabilities does not declare. Either add the capability the ` +
        `operator actually has, or drop the config entry. The control renders nothing ` +
        `until the two agree.`,
    );
  }
}
